import React, { useState, useRef, useMemo, memo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from 'next/image';
import { Image as ImageIcon, ChevronLeft, ChevronRight, Star, Newspaper, Eye, FileSearch, MessageSquareText, Share2, Flag } from "lucide-react";
import { getCategoryById } from "../../utils/projectCategories";
import { apiOnlyCategorizeApplication } from "../../utils/categorizationHelper";
import { getPlanningDocumentUrl } from "../../utils/openPlanningDoc";
import { getMapSnapshotUrl, MAP_SNAPSHOT_ATTRIBUTION } from "../../lib/planning/mapSnapshotUrl";
import { Spinner } from "./spinner";
import { useSettings } from "../../lib/hooks/useSettings";
import ConditionalIcon from "./ConditionalIcon";
import ShareButton from "./ShareButton";
import { usePlanningContext } from "../planning/state/PlanningContext";
import EnhancedReferenceDisplay from "./EnhancedReferenceDisplay";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from "./dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./tooltip";
import { buildFormDataSummary, extractInlineApplicationFormData, hasApplicationFormData } from "../../lib/planning/formData";
import { buildPlanningApplicationUrl } from "../../lib/planningApplicationUrl";
import { fetchApplicationById } from "../../lib/services/applicationsApiService";
import { scheduleApplicationDetailHydration } from "../../lib/services/applicationDetailHydrationQueue";
import { formatCouncilQualifiedReference, isAggregateCouncilScope, normalizeCouncilSlug } from "../../lib/councils";
import {
  getDiscussionCount,
  getDiscussionPreviewComments,
  getDiscussionThreadCount,
  hydrateDiscussionSummary,
  subscribeToDiscussionUpdates,
  type PlanningDiscussionPreviewComment,
} from "../../lib/services/planningDiscussionsService";
import { usePlanningDiscussionsFeatureFlag } from "../../lib/planningDiscussionFeatureFlag";
import { normalizePlanningStatus } from "../../lib/planning/status";
import PlanningSignalChips from "../planning/signals/PlanningSignalChips";

// Document object interface
interface Document {
  thumbnail_url: string; // Always present
  doc_link?: string; // May be empty
  document_url?: string;
  pdf_url?: string;
  url?: string;
  link?: string;
  thumbnail?: string;
  source_desc?: string;
  title?: string;
  name?: string;
}

interface Application {
  ai_summary?: string;
  description?: string;
  address?: string;
  reference_no?: string;
  received_date?: string;
  validated_date?: string;
  status?: string | null;
  link?: string;
  // Updated: documents is now an array of Document objects
  documents?: Document[];
  image_url?: string;
  thumbnail_url?: string;
  thumbnail_urls?: string[];
  latitude?: number | string | null;
  longitude?: number | string | null;
  lat?: number | string | null;
  lng?: number | string | null;
  lon?: number | string | null;
  // Legacy format for backward compatibility
  documents_legacy?: string;
  // new
  scale_rating?: number; // Ensure we can store numeric scale rating
  interesting?: boolean | null;
  interest_confidence?: number | null;
  interest_reason?: string | null;
  interest_signals?: string[];
  // Alternative field names as fallbacks
  proposal?: string;
  details?: string;
  ref?: string;
  location?: string;
  category?: string | null;
  has_application_form?: boolean;
  application_form?: unknown;
  applicationForm?: unknown;
  application_form_data?: unknown;
  application_form_json?: unknown;
  application_form_parsed?: unknown;
  form_data?: unknown;
  form?: unknown;
  parsed_form?: unknown;
  ai_match_data?: {
    development_name: string;
    development_aliases: string[];
    is_combined_application: boolean;
  };
  record_type?: string;
  data_source?: string;
  [key: string]: unknown;
}

const EMPTY_APPLICATION: Application = { documents: [] };
const DEFAULT_CATEGORY_ICON_SRC = '/PlanningPageCategories/thumbs/Legal & Administrative.png';
const hydratedApplicationCache = new Map<string, Application | null>();
const hydratedApplicationPromiseCache = new Map<string, Promise<Application | null>>();

const getThumbnailSrc = (url?: string | null): string => {
  if (!url) return '/placeholder-image.jpg';
  const trimmed = url.trim();
  return trimmed.length > 0 ? trimmed : '/placeholder-image.jpg';
};

const getApplicationReference = (application?: Application | null): string => {
  const rawReference =
    application?.reference_no ||
    application?.ref ||
    application?.reference ||
    application?.application_ref;
  return typeof rawReference === 'string' ? rawReference.trim() : '';
};

const getApplicationCouncil = (
  application?: Application | null,
  fallbackCouncil?: string
): string | undefined => {
  const rawCouncil =
    application?.source_council ||
    application?.council_slug ||
    application?.council ||
    application?.sourceCouncil;
  const normalized = typeof rawCouncil === 'string'
    ? normalizeCouncilSlug(rawCouncil)
    : null;
  if (normalized) return normalized;

  if (fallbackCouncil && !isAggregateCouncilScope(fallbackCouncil)) {
    return normalizeCouncilSlug(fallbackCouncil) || undefined;
  }

  return undefined;
};

const getApplicationFeedAddedDate = (application?: Application | null): string | null => {
  if (!application) return null;
  const value = application.created_at || application.scraped_date || null;
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

const countPreviewedDiscussionComments = (comments: PlanningDiscussionPreviewComment[]): number =>
  comments.reduce((total, comment) => total + 1 + comment.replyCount, 0);

const formatDiscussionPreviewTimestamp = (value?: string | null): string => {
  if (!value) return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Just now';
  }

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
  }).format(date);
};

const getDiscussionAuthorInitials = (label?: string | null): string => {
  const parts = String(label || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return 'CS';
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
};

const hasInlineDocuments = (application?: Application | null): boolean => {
  if (!application) return false;
  if (Array.isArray(application.documents) && application.documents.length > 0) return true;
  if (typeof application.documents_legacy === 'string' && application.documents_legacy.trim().length > 0) {
    return true;
  }
  return false;
};

const hasDescriptionContent = (application?: Application | null): boolean => {
  if (!application) return false;
  const candidates = [
    application.ai_summary,
    application.description,
    application.proposal,
    application.details,
  ];
  return candidates.some((value) => typeof value === 'string' && value.trim().length > 0);
};

const isPlanningApplication = (application?: Application | null): boolean => {
  if (!application) return false;
  if (application.record_type === 'short_term_let' || application.data_source === 'short_term_let') {
    return false;
  }
  const reference = getApplicationReference(application).toUpperCase();
  return !reference.endsWith('/FULSTL') && !reference.endsWith('/CLESTL') && !reference.endsWith('/CLPSTL');
};

const isSparseApplication = (application?: Application | null): boolean => {
  if (!application) return false;
  const hasDescription = hasDescriptionContent(application);
  const hasDocuments = hasInlineDocuments(application);
  const hasStatus = typeof application.status === 'string' && application.status.trim().length > 0;
  const hasCouncilLink = typeof application.link === 'string' && application.link.trim().length > 0 && application.link !== '#';
  return !hasDescription || (!hasDocuments && !hasStatus && !hasCouncilLink);
};

interface GroupedApplicationsCardProps {
  group: {
    address?: string;
    baseAddress?: string;
    label?: string;
    apps: Application[];
  };
  // lightMode: boolean; // Removed prop
  // vintageMode?: boolean; // Removed prop
  showAiSummaries: boolean;
  // selectedImage: string | null; // Removed prop
  setSelectedImage: (url: string | null) => void;
  isExpanded?: boolean; // External control for expanded state
  onToggleExpand?: () => void; // Handler for expanding/collapsing
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onOpenApplicationModal?: (app: Application, options?: { initialTab?: string; initialDocumentUrl?: string | null; initialImageExpanded?: boolean }) => void; // Optional modal handler
}

const GroupedApplicationsCard: React.FC<GroupedApplicationsCardProps> = ({
  group,
  // lightMode, // Removed prop
  // vintageMode = false, // Removed prop
  showAiSummaries,
  // selectedImage, // Removed prop
  isFavorite = false,
  onToggleFavorite,
  onOpenApplicationModal,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [docIndex, setDocIndex] = useState(0); // index for documents within current application
  const [direction, setDirection] = useState(0); // -1 for left, +1 for right
  const [mapSnapshotReady, setMapSnapshotReady] = useState(false);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    hasMountedRef.current = true;
    setMapSnapshotReady(true);
  }, []);
  // Removed local report modal in favor of opening the application modal's Reports tab

  const apps = group.apps;
  const baseCurrentApp = apps[currentIndex] ?? EMPTY_APPLICATION;
  const planningContext = usePlanningContext();
  const pageCouncilSlug = typeof planningContext?.councilSlug === 'string'
    ? planningContext.councilSlug
    : undefined;
  const currentAppReference = useMemo(
    () => getApplicationReference(baseCurrentApp),
    [baseCurrentApp]
  );
  const currentAppCouncil = useMemo(
    () => getApplicationCouncil(baseCurrentApp, pageCouncilSlug),
    [baseCurrentApp, pageCouncilSlug]
  );
  const currentAppHydrationKey = useMemo(
    () => currentAppReference && currentAppCouncil
      ? `${currentAppCouncil}:${currentAppReference}`
      : '',
    [currentAppCouncil, currentAppReference]
  );
  const [hydratedCurrentApp, setHydratedCurrentApp] = useState<Application | null>(null);
  const [isHydratingCurrentApp, setIsHydratingCurrentApp] = useState(false);
  const shouldHydrateCurrentApp = useMemo(
    () =>
      !baseCurrentApp.source_record_only && Boolean(currentAppReference) &&
      Boolean(currentAppCouncil) &&
      isPlanningApplication(baseCurrentApp) &&
      isSparseApplication(baseCurrentApp),
    [baseCurrentApp, currentAppCouncil, currentAppReference]
  );

  useEffect(() => {
    if (!currentAppHydrationKey) {
      setHydratedCurrentApp(null);
      setIsHydratingCurrentApp(false);
      return;
    }

    const cached = hydratedApplicationCache.get(currentAppHydrationKey) ?? null;
    setHydratedCurrentApp(cached);
    setIsHydratingCurrentApp(false);
  }, [currentAppHydrationKey]);

  useEffect(() => {
    if (!shouldHydrateCurrentApp || !currentAppReference || !currentAppCouncil || !currentAppHydrationKey) {
      setIsHydratingCurrentApp(false);
      return;
    }

    if (hydratedApplicationCache.has(currentAppHydrationKey)) {
      setHydratedCurrentApp(hydratedApplicationCache.get(currentAppHydrationKey) ?? null);
      setIsHydratingCurrentApp(false);
      return;
    }

    let cancelled = false;
    setIsHydratingCurrentApp(true);

    const existingPromise = hydratedApplicationPromiseCache.get(currentAppHydrationKey);
    const hydrationPromise = existingPromise || scheduleApplicationDetailHydration(async () => {
      try {
        const record = await fetchApplicationById(currentAppReference, {
          council: currentAppCouncil,
          retries: 1,
          timeoutMs: 10000,
        });
        return record && typeof record === 'object' ? (record as Application) : null;
      } catch (error) {
        console.warn('GroupedApplicationsCard detail hydration failed', currentAppReference, error);
        return null;
      }
    });

    if (!existingPromise) {
      hydratedApplicationPromiseCache.set(currentAppHydrationKey, hydrationPromise);
    }

    hydrationPromise
      .then((record) => {
        hydratedApplicationCache.set(currentAppHydrationKey, record);
        if (!cancelled) {
          setHydratedCurrentApp(record);
        }
      })
      .finally(() => {
        hydratedApplicationPromiseCache.delete(currentAppHydrationKey);
        if (!cancelled) {
          setIsHydratingCurrentApp(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentAppCouncil, currentAppHydrationKey, currentAppReference, shouldHydrateCurrentApp]);

  // Ensure we have a valid currentApp without early return
  const currentApp = useMemo(
    () => ({ ...baseCurrentApp, ...(hydratedCurrentApp || {}) }),
    [baseCurrentApp, hydratedCurrentApp]
  );

  // Destructure API fields from current application with robust fallbacks
  const {
    ai_summary,
    description = "",
    address = "Unknown Address",
    reference_no,
    received_date = "",
    validated_date = "",
    status = "",
    link = "#",
    // new
    scale_rating,
    interesting,
    // Alternative field names as fallbacks
    proposal,
    details,
    ref,
    location,
    has_application_form,
  } = currentApp;
  const isWorthWatching = interesting === true;
  const displayStatus = normalizePlanningStatus(status);

  // Create robust field values with multiple fallback options
  const safeReferenceNo = reference_no || ref || '';
  const discussionsFlag = usePlanningDiscussionsFeatureFlag();
  const sourceOnly = currentApp.source_record_only === true;
  const discussionsEnabled = discussionsFlag && !sourceOnly;
  const [discussionCount, setDiscussionCount] = useState(0);
  const [discussionThreadCount, setDiscussionThreadCount] = useState(0);
  const [discussionPreviewComments, setDiscussionPreviewComments] = useState<PlanningDiscussionPreviewComment[]>([]);
  const originalDescription = description || proposal || details || '';
  const displayDescription =
    (showAiSummaries && ai_summary) ||
    originalDescription ||
    (isHydratingCurrentApp ? "Loading application details..." : "No description available.");
  const safeAddress = address || location || 'Unknown Address';
  const inlineFormData = useMemo(
    () => extractInlineApplicationFormData(currentApp as Record<string, unknown>),
    [currentApp]
  );
  const formSummary = useMemo(() => buildFormDataSummary(inlineFormData), [inlineFormData]);
  const hasFormData = hasApplicationFormData(currentApp as Record<string, unknown>) || Boolean(has_application_form);
  const hasExtractedFormData = formSummary.totalFields > 0;
  const formBadgeText = hasExtractedFormData ? `Form ${formSummary.coverage}%` : 'Form PDF';

  useEffect(() => {
    if (!discussionsEnabled || !safeReferenceNo) {
      setDiscussionCount(0);
      setDiscussionThreadCount(0);
      setDiscussionPreviewComments([]);
      return;
    }

    const syncDiscussionCount = () => {
      setDiscussionCount(getDiscussionCount(safeReferenceNo));
      setDiscussionThreadCount(getDiscussionThreadCount(safeReferenceNo));
      setDiscussionPreviewComments(getDiscussionPreviewComments(safeReferenceNo));
    };

    syncDiscussionCount();
    void hydrateDiscussionSummary(safeReferenceNo).catch(() => {
      // Keep the card quiet if discussion summaries fail to load.
    });

    return subscribeToDiscussionUpdates(syncDiscussionCount);
  }, [discussionsEnabled, safeReferenceNo]);

  // Modal share URL (deep link to open application modal directly)
  const modalShareUrl = useMemo(() => {
    try {
      return buildPlanningApplicationUrl(safeReferenceNo, currentAppCouncil);
    } catch {
      return typeof window !== 'undefined' ? window.location.href : 'https://www.city-scope.co.uk';
    }
  }, [currentAppCouncil, safeReferenceNo]);

  // Get category information for the current application
  const categoryInfo = useMemo(() => {
    try {
      // Use API-only categorization - no complex fallbacks
      const tempApp = {
        category: currentApp.category,
        description: displayDescription?.trim() || '',
        reference_no: safeReferenceNo
      };
      
      const { primaryCategory } = apiOnlyCategorizeApplication(tempApp);
      
      // Get category data including uncategorized
      const catData = getCategoryById(primaryCategory);
      
      if (catData && catData.id && catData.label && catData.icon) {
        return catData;
      }
      
      // Final fallback to uncategorized if something goes wrong
      return getCategoryById('uncategorized') || {
        id: 'uncategorized',
        label: 'Uncategorised',
        icon: DEFAULT_CATEGORY_ICON_SRC
      };
      
    } catch (error) {
      console.warn('Error in categoryInfo memo:', error);
      // Error fallback to uncategorized
      return getCategoryById('uncategorized') || {
        id: 'uncategorized',
        label: 'Uncategorised',
        icon: DEFAULT_CATEGORY_ICON_SRC
      };
    }
  }, [displayDescription, safeReferenceNo, currentApp.category]);

  // helper to color-code the scale rating with civic noir tokens (shared with slider)
  const getScaleRatingColor = (rating: number) => {
    if (rating >= 7) {
      return {
        bg: "bg-[var(--c-error)]",
        text: "text-[var(--c-error)]",
        label: "High Impact",
        color: "var(--c-error)",
      };
    } else if (rating >= 4) {
      return {
        bg: "bg-[var(--c-warning)]",
        text: "text-[var(--c-warning)]",
        label: "Medium Impact",
        color: "var(--c-warning)",
      };
    } else if (rating >= 1) {
      return {
        bg: "bg-[var(--c-success)]",
        text: "text-[var(--c-success)]",
        label: "Low Impact",
        color: "var(--c-success)",
      };
    } else {
      return {
        bg: "bg-[var(--c-text-disabled)]",
        text: "text-[var(--c-text-disabled)]",
        label: "Unrated",
        color: "var(--c-text-disabled)",
      };
    }
  };

  const openApplicationModalWithDocument = (
    app: Application,
    documentUrl?: string | null,
    e?: React.MouseEvent<HTMLElement>
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    openApplicationModal(app, {
      initialTab: 'overview',
      initialDocumentUrl: documentUrl || null,
      initialImageExpanded: true,
    });
  };

  const handleOpenDiscussion = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (safeReferenceNo) {
      openApplicationModal(currentApp, { initialTab: 'discussion' });
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
  const addedDateValue = getApplicationFeedAddedDate(currentApp);
  const feedDateLabel = addedDateValue
    ? `Added ${formatDate(addedDateValue)}`
    : validated_date
    ? `Validated ${formatDate(validated_date)}`
    : received_date
    ? `Received ${formatDate(received_date)}`
    : '';

  // Get documents from either new format or legacy format
  const documentsList = useMemo(() => {
    // Ensure currentApp.documents is an array before processing
    const appDocs = currentApp?.documents;
    if (Array.isArray(appDocs) && appDocs.length > 0) {
      return appDocs;
    }
    // Legacy format handling (if currentApp.documents_legacy exists)
    if (currentApp?.documents_legacy) {
      const urls = currentApp.documents_legacy
        .split(",")
        .map((str:string) => str.trim())
        .filter((str:string) => str.length > 0 && str.startsWith("http"));
      return urls.map((urlStr:string) => ({ thumbnail_url: urlStr, doc_link: urlStr, document_url: urlStr })); // Treat legacy as links
    }

    if (Array.isArray(currentApp?.thumbnail_urls) && currentApp.thumbnail_urls.length > 0) {
      return Array.from(new Set(
        currentApp.thumbnail_urls
          .filter((value): value is string => typeof value === 'string')
          .map((value) => value.trim())
          .filter(Boolean)
      )).map((thumbnailUrl) => ({
        thumbnail_url: thumbnailUrl,
        doc_link: '',
        document_url: thumbnailUrl,
      }));
    }

    const fallbackThumbnail =
      typeof currentApp?.image_url === 'string' && currentApp.image_url.trim()
        ? currentApp.image_url.trim()
        : typeof currentApp?.thumbnail_url === 'string' && currentApp.thumbnail_url.trim()
          ? currentApp.thumbnail_url.trim()
          : '';

    if (fallbackThumbnail) {
      return [{
        thumbnail_url: fallbackThumbnail,
        doc_link: '',
        document_url: fallbackThumbnail,
      }];
    }

    return [];
  }, [currentApp]);

  // Current document we are showing in hero
  const currentDoc = documentsList.length > 0 ? documentsList[docIndex % documentsList.length] : null;
  const rawThumbnailUrl = currentDoc?.thumbnail_url?.trim() || '';
  const currentThumbnailSrc = getThumbnailSrc(currentDoc?.thumbnail_url);
  const currentDocUrl = getPlanningDocumentUrl(currentDoc);
  const mapSnapshotUrl = useMemo(
    () => mapSnapshotReady ? getMapSnapshotUrl(currentApp) : null,
    [currentApp, mapSnapshotReady]
  );

  // Reset doc index when switching to a different application
  useEffect(() => {
    setDocIndex(0);
  }, [currentIndex]);

  // Document navigation helpers
  const goToNextDoc = () => {
    if (documentsList.length > 1) {
      setDocIndex((prev) => (prev + 1) % documentsList.length);
    }
  };

  const goToPrevDoc = () => {
    if (documentsList.length > 1) {
      setDocIndex((prev) => (prev - 1 + documentsList.length) % documentsList.length);
    }
  };
  const touchStartX = useRef<number | null>(null);
  const handleMediaTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };
  const handleMediaTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const startX = touchStartX.current;
    touchStartX.current = null;
    const endX = event.changedTouches[0]?.clientX;
    if (startX == null || endX == null || documentsList.length < 2) return;
    const deltaX = endX - startX;
    if (Math.abs(deltaX) < 45) return;
    event.stopPropagation();
    if (deltaX < 0) goToNextDoc();
    else goToPrevDoc();
  };

  // Card images open the application detail modal, with the clicked image selected.
  const handleDocumentClick = (doc: Document, app: Application, e?: React.MouseEvent<HTMLElement>) => {
    const initialDocumentUrl = getPlanningDocumentUrl(doc) || doc.thumbnail_url || doc.thumbnail || null;
    openApplicationModalWithDocument(app, initialDocumentUrl, e);
  };

  // Updated status colors using Neo-Civic color tokens
  const getStatusColor = (s: string) => {
    if (s.includes("Awaiting decision"))
      return {
        bg: "bg-[var(--c-led-medium)]",
        text: "text-[var(--c-led-medium)]",
        border: "border-[var(--c-led-medium)]/30",
        light: "bg-[var(--c-led-medium)]/20",
        colorRaw: "var(--c-led-medium)",
      };
    if (s.includes("Awaiting Assessment"))
      return {
        bg: "bg-[var(--c-accent)]",
        text: "text-[var(--c-accent)]",
        border: "border-[var(--c-accent)]/30",
        light: "bg-[var(--c-accent)]/20",
        colorRaw: "var(--c-accent)",
      };
    if (s.includes("Approved"))
      return {
        bg: "bg-[var(--c-led-low)]",
        text: "text-[var(--c-led-low)]",
        border: "border-[var(--c-led-low)]/30",
        light: "bg-[var(--c-led-low)]/20",
        colorRaw: "var(--c-led-low)",
      };
    if (s.includes("Rejected"))
      return {
        bg: "bg-[var(--c-error)]",
        text: "text-[var(--c-error)]",
        border: "border-[var(--c-error)]/30",
        light: "bg-[var(--c-error)]/20",
        colorRaw: "var(--c-error)",
      };
    return {
      bg: "bg-[var(--c-text-disabled)]",
      text: "text-[var(--c-text-disabled)]",
      border: "border-[var(--c-text-disabled)]/30",
      light: "bg-[var(--c-text-disabled)]/20",
      colorRaw: "var(--c-text-disabled)",
    };
  };

  const statusColor = getStatusColor(displayStatus);

  // Application carousel navigation functions
  const goToNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % apps.length);
  };

  const goToPrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + apps.length) % apps.length);
  };

  // Animation variants
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 20 : -20,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 20 : -20,
      opacity: 0
    })
  };

  const iconRef = useRef<HTMLDivElement>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  // Ensure we have a valid currentApp without early return
  const [imageLoaded, setImageLoaded] = useState(false);

  const settings = useSettings();
  const { 
    openApplicationModal: contextOpenModal, 
    setCategoryFilterMode, 
    setMultipleCategoryFilters, 
    setSelectedCategoryFilter, 
    showToast,
    categoryFilterMode,
    multipleCategoryFilters
  } = planningContext || {};

  // Use prop if provided, otherwise fallback to context
  const openApplicationModal = onOpenApplicationModal || contextOpenModal;

  // Handle card click to open modal
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger modal if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('[role="button"]') ||
      target.closest('input') ||
      target.closest('select')
    ) {
      return;
    }
    
    // Open modal with current application
    openApplicationModal(currentApp);
  };

  const discussionCountLabel =
    discussionCount === 1 ? '1 comment' : `${discussionCount} comments`;
  const discussionThreadLabel =
    discussionThreadCount === 1 ? '1 thread' : `${discussionThreadCount} threads`;
  const previewVisibleCommentCount = useMemo(
    () => countPreviewedDiscussionComments(discussionPreviewComments),
    [discussionPreviewComments]
  );
  const hiddenPreviewCommentCount = Math.max(0, discussionCount - previewVisibleCommentCount);
  const expanded = false;

  return (
    <div
      className="planning-application-card pr-application-card group/card overflow-hidden rounded-md shadow-none relative flex flex-col h-full bg-[var(--c-surface)] border border-[var(--c-border)] cursor-pointer transition-colors duration-150 hover:border-[var(--c-text-tertiary)]"
      onClick={handleCardClick}
    >
      {/* Scale rating stripe — top edge, color-coded by impact level */}
      <div
        className="pr-application-card-accent absolute inset-x-0 top-0 z-20"
        style={{ height: '3px', backgroundColor: getScaleRatingColor(scale_rating || 0).color }}
      />

      {/* ── Image Carousel — hero at top ── */}
      <div className="pr-application-card-media-wrap relative overflow-hidden">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentIndex}
            variants={variants}
            custom={direction}
            initial={hasMountedRef.current ? "enter" : false}
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="relative"
          >
            {/* Image container */}
            <div
              className="pr-application-card-media group relative w-full aspect-[4/3] overflow-hidden bg-[var(--c-bg)] touch-pan-y"
              onTouchStart={handleMediaTouchStart}
              onTouchEnd={handleMediaTouchEnd}
            >

              {currentDoc ? (
                currentDocUrl ? (
                  /* Branch 1 — image with openable document link */
                  <div className="w-full h-full block relative">
                    <Image
                      src={currentThumbnailSrc}
                      alt="Open document"
                      className={`pr-application-card-image w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out ${imageLoaded ? '' : 'opacity-0'}`}
                      fill={true}
                      style={{ objectFit: 'cover' }}
                      unoptimized={true}
                      onLoad={() => setImageLoaded(true)}
                    />
                    {!imageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[var(--c-bg)]/50">
                        <Spinner size={28} className="text-[var(--c-accent)]" />
                      </div>
                    )}
                    {/* Center click area for application detail image */}
                    <button
                      onClick={(e) => handleDocumentClick(currentDoc, currentApp, e)}
                      className="pr-application-card-media-button absolute inset-0 w-full h-full cursor-pointer z-20 flex items-center justify-center group/center"
                      aria-label="Open application image in details"
                    >
                      <div className="pr-application-card-media-action opacity-0 group-hover/center:opacity-100 transition-all duration-300 bg-[var(--dock-bg)] backdrop-blur-md rounded-full p-3 shadow-xl border border-[var(--dock-border)]/60">
                        <Eye className="w-5 h-5 text-[var(--c-accent)]" aria-hidden="true" />
                      </div>
                    </button>

                    {/* Doc navigation arrows */}
                    {documentsList.length > 1 && (
                      <>
                        <motion.button
                          onClick={(e) => { e.stopPropagation(); goToPrevDoc(); }}
                          className="pr-application-card-media-nav pr-application-card-media-nav--prev absolute left-0 top-0 w-16 h-full flex items-center justify-center bg-transparent hover:bg-gradient-to-r hover:from-black/20 hover:to-transparent transition-all duration-300 z-30 group/nav-left"
                          whileTap={{ scale: 0.95 }}
                          aria-label="Previous image"
                        >
                          <motion.div
                            className="pr-application-card-media-nav-icon w-10 h-10 rounded-full bg-[var(--dock-bg)] backdrop-blur-xl border border-[var(--dock-border)] flex items-center justify-center text-[var(--c-text-primary)] shadow-2xl opacity-90 sm:opacity-0 sm:group-hover:opacity-100 sm:group-hover/nav-left:opacity-100 transition-all duration-300"
                            whileHover={settings.shouldShowAnimations ? { scale: 1.1, backgroundColor: "var(--c-surface)", borderColor: "var(--c-accent)", boxShadow: "0 0 20px rgba(0,0,0,0.3)" } : {}}
                            initial={settings.shouldShowAnimations ? { x: -10 } : { x: 0 }}
                            animate={{ x: 0 }}
                          >
                            <ConditionalIcon icon={<ChevronLeft size={18} className="text-[var(--c-accent)]" />} type="navigation" fallbackText="‹" />
                          </motion.div>
                        </motion.button>
                        <motion.button
                          onClick={(e) => { e.stopPropagation(); goToNextDoc(); }}
                          className="pr-application-card-media-nav pr-application-card-media-nav--next absolute right-0 top-0 w-16 h-full flex items-center justify-center bg-transparent hover:bg-gradient-to-l hover:from-black/20 hover:to-transparent transition-all duration-300 z-30 group/nav-right"
                          whileTap={{ scale: 0.95 }}
                          aria-label="Next image"
                        >
                          <motion.div
                            className="pr-application-card-media-nav-icon w-10 h-10 rounded-full bg-[var(--dock-bg)] backdrop-blur-xl border border-[var(--dock-border)] flex items-center justify-center text-[var(--c-text-primary)] shadow-2xl opacity-90 sm:opacity-0 sm:group-hover:opacity-100 sm:group-hover/nav-right:opacity-100 transition-all duration-300"
                            whileHover={settings.shouldShowAnimations ? { scale: 1.1, backgroundColor: "var(--c-surface)", borderColor: "var(--c-accent)", boxShadow: "0 0 20px rgba(0,0,0,0.3)" } : {}}
                            initial={settings.shouldShowAnimations ? { x: 10 } : { x: 0 }}
                            animate={{ x: 0 }}
                          >
                            <ConditionalIcon icon={<ChevronRight size={18} className="text-[var(--c-accent)]" />} type="navigation" fallbackText="›" />
                          </motion.div>
                        </motion.button>
                      </>
                    )}

                    {/* Favourite toggle */}
                    <div className="absolute top-3 left-3 flex flex-row gap-2 z-50">
                      {onToggleFavorite && (
                        <motion.button
                          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                          aria-label={isFavorite ? "Remove from favourites" : "Add to favourites"}
                          className={`pr-card-icon-button flex h-11 w-11 items-center justify-center rounded-[3px] border p-0 transition-colors duration-150 sm:h-8 sm:w-8 ${isFavorite ? "border-[var(--c-text-primary)] bg-[var(--c-text-primary)] text-[var(--c-accent)]" : "border-[var(--c-border)] bg-[var(--c-surface)]/95 text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] hover:border-[var(--c-text-tertiary)]"}`}
                          whileTap={{ scale: 0.95 }}
                        >
                          {isFavorite ? <Star size={15} fill="currentColor" /> : <Star size={15} />}
                        </motion.button>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Branch 2 — image without doc link */
                  <div className="w-full h-full relative">
                    <Image
                      src={currentThumbnailSrc}
                      alt="Document thumbnail"
                      className={`pr-application-card-image w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out ${imageLoaded ? '' : 'opacity-0'}`}
                      fill={true}
                      style={{ objectFit: 'cover' }}
                      unoptimized={true}
                      onLoad={() => setImageLoaded(true)}
                    />
                    {!imageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[var(--c-bg)]/50">
                        <Spinner size={28} className="text-[var(--c-accent)]" />
                      </div>
                    )}

                    {/* Center click area for application detail image */}
                    <button
                      onClick={(e) => openApplicationModalWithDocument(currentApp, rawThumbnailUrl, e)}
                      className="pr-application-card-media-button absolute inset-0 w-full h-full cursor-pointer z-20 flex items-center justify-center group/center"
                      aria-label="Open application image in details"
                    >
                      <div className="pr-application-card-media-action opacity-0 group-hover/center:opacity-100 transition-all duration-300 bg-[var(--c-surface)]/95 backdrop-blur-md rounded-full p-3 shadow-xl border border-[var(--c-border)]/50">
                        <ConditionalIcon icon={<ImageIcon size={20} className="text-[var(--c-accent)]" />} type="general" fallbackText="🖼" />
                      </div>
                    </button>

                    {/* Doc navigation arrows */}
                    {documentsList.length > 1 && (
                      <>
                        <motion.button
                          onClick={(e) => { e.stopPropagation(); goToPrevDoc(); }}
                          className="pr-application-card-media-nav pr-application-card-media-nav--prev absolute left-0 top-0 w-16 h-full flex items-center justify-center bg-transparent hover:bg-gradient-to-r hover:from-black/20 hover:to-transparent transition-all duration-300 z-30 group/nav-left"
                          whileTap={{ scale: 0.95 }}
                          aria-label="Previous image"
                        >
                          <motion.div
                            className="pr-application-card-media-nav-icon w-10 h-10 rounded-full bg-[var(--c-surface)]/90 backdrop-blur-xl border border-[var(--c-border)]/70 flex items-center justify-center text-[var(--c-text-primary)] shadow-2xl opacity-90 sm:opacity-0 sm:group-hover:opacity-100 sm:group-hover/nav-left:opacity-100 transition-all duration-300"
                            whileHover={settings.shouldShowAnimations ? { scale: 1.1, backgroundColor: "var(--c-surface)", borderColor: "var(--c-accent)", boxShadow: "0 0 20px rgba(0,0,0,0.3)" } : {}}
                            initial={settings.shouldShowAnimations ? { x: -10 } : { x: 0 }}
                            animate={{ x: 0 }}
                          >
                            <ConditionalIcon icon={<ChevronLeft size={18} className="text-[var(--c-accent)]" />} type="navigation" fallbackText="‹" />
                          </motion.div>
                        </motion.button>
                        <motion.button
                          onClick={(e) => { e.stopPropagation(); goToNextDoc(); }}
                          className="pr-application-card-media-nav pr-application-card-media-nav--next absolute right-0 top-0 w-16 h-full flex items-center justify-center bg-transparent hover:bg-gradient-to-l hover:from-black/20 hover:to-transparent transition-all duration-300 z-30 group/nav-right"
                          whileTap={{ scale: 0.95 }}
                          aria-label="Next image"
                        >
                          <motion.div
                            className="pr-application-card-media-nav-icon w-10 h-10 rounded-full bg-[var(--c-surface)]/90 backdrop-blur-xl border border-[var(--c-border)]/70 flex items-center justify-center text-[var(--c-text-primary)] shadow-2xl opacity-90 sm:opacity-0 sm:group-hover:opacity-100 sm:group-hover/nav-right:opacity-100 transition-all duration-300"
                            whileHover={settings.shouldShowAnimations ? { scale: 1.1, backgroundColor: "var(--c-surface)", borderColor: "var(--c-accent)", boxShadow: "0 0 20px rgba(0,0,0,0.3)" } : {}}
                            initial={settings.shouldShowAnimations ? { x: 10 } : { x: 0 }}
                            animate={{ x: 0 }}
                          >
                            <ConditionalIcon icon={<ChevronRight size={18} className="text-[var(--c-accent)]" />} type="navigation" fallbackText="›" />
                          </motion.div>
                        </motion.button>
                      </>
                    )}

                    {/* Favourite toggle */}
                    <div className="absolute top-3 left-3 flex flex-row gap-2 z-50">
                      {onToggleFavorite && (
                        <motion.button
                          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                          aria-label={isFavorite ? "Remove from favourites" : "Add to favourites"}
                          className={`pr-card-icon-button flex h-11 w-11 items-center justify-center rounded-[3px] border p-0 transition-colors duration-150 sm:h-8 sm:w-8 ${isFavorite ? "border-[var(--c-text-primary)] bg-[var(--c-text-primary)] text-[var(--c-accent)]" : "border-[var(--c-border)] bg-[var(--c-surface)]/95 text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] hover:border-[var(--c-text-tertiary)]"}`}
                          whileTap={{ scale: 0.95 }}
                        >
                          {isFavorite ? <Star size={15} fill="currentColor" /> : <Star size={15} />}
                        </motion.button>
                      )}
                    </div>
                  </div>
                )
              ) : (
                /* Branch 3 — no document */
                <div className="w-full h-full flex items-center justify-center bg-[var(--c-surface)] relative">
                  {mapSnapshotUrl ? (
                    <>
                      <Image
                        src={mapSnapshotUrl}
                        alt="Location map"
                        className="pr-application-card-image w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                        fill={true}
                        style={{ objectFit: 'cover' }}
                        unoptimized={true}
                      />
                      <div className="pointer-events-none absolute bottom-2 right-2 z-[26] border border-white/60 bg-white/90 px-2 py-1 text-[9px] leading-none text-black shadow-sm">
                        {MAP_SNAPSHOT_ATTRIBUTION}
                      </div>
                      <button
                        onClick={(e) => openApplicationModalWithDocument(currentApp, null, e)}
                        className="pr-application-card-media-button absolute inset-0 w-full h-full cursor-pointer z-20 flex items-center justify-center group/center"
                        aria-label="Open location map in details"
                      >
                        <div className="pr-application-card-media-action opacity-0 group-hover/center:opacity-100 transition-all duration-300 bg-[var(--c-surface)]/95 backdrop-blur-md rounded-full p-3 shadow-xl border border-[var(--c-border)]/50">
                          <ConditionalIcon icon={<ImageIcon size={20} className="text-[var(--c-accent)]" />} type="general" fallbackText="Map" />
                        </div>
                      </button>
                    </>
                  ) : (
                    <div className="text-[var(--c-text-disabled)] opacity-30">
                      <ImageIcon size={36} />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex flex-row gap-2 z-50">
                    <ShareButton
                      application={{ reference: safeReferenceNo, description: originalDescription, aiSummary: ai_summary, location: safeAddress, status: status || 'Status Unknown' }}
                      documents={documentsList.map(d => ({...d, document_url: d.doc_link}))}
                    />
                    {onToggleFavorite && (
                      <motion.button
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                        aria-label={isFavorite ? "Remove from favourites" : "Add to favourites"}
                        className={`pr-card-icon-button flex h-11 w-11 items-center justify-center rounded-[3px] border p-0 transition-colors duration-150 sm:h-8 sm:w-8 ${isFavorite ? "border-[var(--c-text-primary)] bg-[var(--c-text-primary)] text-[var(--c-accent)]" : "border-[var(--c-border)] bg-[var(--c-surface)]/95 text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] hover:border-[var(--c-text-tertiary)]"}`}
                        whileTap={{ scale: 0.95 }}
                      >
                        {isFavorite ? <Star size={15} fill="currentColor" /> : <Star size={15} />}
                      </motion.button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Unified overlays — rendered on top of all three image branches ── */}

              {/* Address overlay at image bottom */}
              <div className="pr-application-card-title-overlay absolute inset-x-0 bottom-0 z-[25] pointer-events-none bg-gradient-to-t from-black/85 via-black/55 to-transparent pt-14 pb-3 px-4">
                {(() => {
                  const hasProjectData = apps.some(app => app.ai_match_data?.development_name);
                  const projectApp = apps.find(app => app.ai_match_data?.development_name);
                  const projectName = projectApp?.ai_match_data?.development_name;
                  return (
                    <>
                      <p className="pr-application-card-title font-['Newsreader',Georgia,serif] font-medium text-white text-lg sm:text-xl leading-snug line-clamp-2">
                        {hasProjectData && projectName
                          ? projectName
                          : (group.label || group.baseAddress || group.address || 'Unknown Location')}
                      </p>
                      {hasProjectData && (
                        <p className="pr-application-card-subtitle text-xs text-white/70 mt-0.5 line-clamp-1">
                          {group.label || group.baseAddress || group.address || 'Unknown Location'}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Top-right badge stack: doc counter + category icon */}
              <div className="pr-application-card-badge-stack absolute top-3 right-3 z-[50] flex flex-col items-end gap-1.5">
                {documentsList.length > 1 && (
                  <div className="pr-application-card-badge inline-flex items-center gap-1 rounded-[3px] border border-[var(--c-border)] bg-[var(--c-surface)]/95 px-2 py-0.5 font-['DM_Mono'] text-[10px] font-medium text-[var(--c-text-primary)]">
                    <ImageIcon size={11} className="opacity-70" />
                    {docIndex + 1}/{documentsList.length}
                  </div>
                )}
                {categoryInfo && categoryInfo.icon && categoryInfo.label && (
                  <>
                    {/* Tooltip wraps only the badge trigger — Dialog is state-controlled separately */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <motion.div
                            ref={iconRef}
                            className="pr-application-card-badge cursor-pointer rounded-[3px] border border-[var(--c-border)] bg-[var(--c-surface)]/95 p-1.5 transition-colors hover:border-[var(--c-text-tertiary)] focus:outline-none"
                            role="button"
                            aria-label={`Category: ${categoryInfo.label}`}
                            aria-disabled={sourceOnly}
                            tabIndex={sourceOnly ? -1 : 0}
                            onClick={(e) => { e.stopPropagation(); if (!sourceOnly) setCategoryDialogOpen(true); }}
                          >
                            <Image
                              src={categoryInfo.icon}
                              alt=""
                              aria-hidden="true"
                              width={22}
                              height={22}
                              unoptimized
                              className="object-contain"
                              onError={(event) => { (event.currentTarget as HTMLImageElement).src = DEFAULT_CATEGORY_ICON_SRC; }}
                            />
                          </motion.div>
                        </TooltipTrigger>
                        <TooltipContent
                          className="bg-[var(--dock-bg)] border-[var(--dock-border)] text-[var(--c-text-primary)] !z-[9999]"
                          side="left"
                          sideOffset={8}
                        >
                          {categoryInfo.label}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Category dialog — state-controlled, no trigger nesting */}
                    <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                      <DialogContent className="bg-[var(--c-surface)] border border-[var(--c-border)]">
                        <DialogTitle className="text-[var(--c-text-primary)]">{categoryInfo.label}</DialogTitle>
                        <DialogDescription className="text-[var(--c-text-secondary)]">
                          This card is categorized as &quot;{categoryInfo.label}&quot;. Apply a filter to see similar applications.
                        </DialogDescription>
                        <div className="mt-3 flex items-center gap-3">
                          <div className="rounded-md p-2 bg-[var(--dock-bg)] border border-[var(--dock-border)]">
                            <Image src={categoryInfo.icon} alt="" width={40} height={40} unoptimized className="object-contain" />
                          </div>
                          <div className="flex gap-2">
                            <DialogClose asChild>
                              <button
                                className="btn btn--primary"
                                onClick={() => {
                                  const current = Array.isArray(multipleCategoryFilters) ? multipleCategoryFilters : [];
                                  if (categoryFilterMode !== 'include') { setCategoryFilterMode('include'); setMultipleCategoryFilters([categoryInfo.id]); }
                                  else { setMultipleCategoryFilters(Array.from(new Set([...current, categoryInfo.id]))); }
                                  setSelectedCategoryFilter(null);
                                  const count = Array.isArray(multipleCategoryFilters) ? new Set([...multipleCategoryFilters, categoryInfo.id]).size : 1;
                                  showToast(`Including ${categoryInfo.label}${count > 1 ? ` (+${count-1} stacked)` : ''}`, 'info');
                                }}
                              >Include</button>
                            </DialogClose>
                            <DialogClose asChild>
                              <button
                                className="btn"
                                onClick={() => {
                                  const current = Array.isArray(multipleCategoryFilters) ? multipleCategoryFilters : [];
                                  if (categoryFilterMode !== 'exclude') { setCategoryFilterMode('exclude'); setMultipleCategoryFilters([categoryInfo.id]); }
                                  else { setMultipleCategoryFilters(Array.from(new Set([...current, categoryInfo.id]))); }
                                  setSelectedCategoryFilter(null);
                                  const count = Array.isArray(multipleCategoryFilters) ? new Set([...multipleCategoryFilters, categoryInfo.id]).size : 1;
                                  showToast(`Excluding ${categoryInfo.label}${count > 1 ? ` (+${count-1} stacked)` : ''}`, 'info');
                                }}
                              >Exclude</button>
                            </DialogClose>
                          </div>
                        </div>
                        <div className="mt-4 flex gap-3">
                          <DialogClose asChild>
                            <button
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--c-accent)] bg-[var(--c-accent)] text-[var(--c-bg)] text-xs shadow-[0_2px_8px_rgba(211,175,55,0.25)] hover:brightness-110"
                              onClick={() => openApplicationModal(currentApp)}
                            >
                              <Eye size={16} /> Open details
                            </button>
                          </DialogClose>
                          {currentApp.link && currentApp.link !== '#' && (
                            <a href={currentApp.link} target="_blank" rel="noopener noreferrer" className="btn">Full application (council site)</a>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>

            </div>
            {/* ── End image container ── */}

            {/* ── Content Section ── */}
            <div className="pr-application-card-body px-4 pt-3 pb-3 flex flex-col gap-3">

              {/* Status row — left: status + project, right: scale + form */}
              <div className="pr-application-card-meta-row flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2.5">
                  {/* Status */}
                  <span
                    className="pr-application-card-status inline-flex min-w-0 max-w-full items-center gap-1.5 font-['DM_Mono'] text-[11px] uppercase tracking-[0.05em] text-[var(--c-text-secondary)]"
                    title={displayStatus}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusColor.bg}`} />
                    <span className="min-w-0 truncate">{displayStatus}</span>
                  </span>
                  {currentApp.ai_match_data?.is_combined_application && (
                    <span className="inline-flex shrink-0 items-center font-['DM_Mono'] text-[11px] uppercase tracking-[0.05em] text-[var(--c-info)]">
                      Project
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/* Scale */}
                  {scale_rating && scale_rating > 0 && (
                    <span className={`pr-application-card-scale inline-flex shrink-0 items-center gap-1.5 font-['DM_Mono'] text-[11px] uppercase tracking-[0.05em] ${getScaleRatingColor(scale_rating).text}`}>
                      Scale {scale_rating}
                    </span>
                  )}
                  {/* Form icon — icon-only with tooltip */}
                  {hasFormData && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="pr-application-card-icon-badge inline-flex items-center justify-center h-6 w-6 rounded-full bg-[var(--c-accent)]/10 text-[var(--c-accent)] cursor-default"
                          >
                            <FileSearch size={13} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          className="bg-[var(--dock-bg)] border-[var(--dock-border)] text-[var(--c-text-primary)] z-[9999] max-w-[200px]"
                          side="top"
                          sideOffset={8}
                        >
                          {hasExtractedFormData
                            ? `Form data: ${formSummary.filledFields}/${formSummary.totalFields} fields extracted (${formBadgeText})`
                            : 'Application form PDF available'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>

              {isWorthWatching && (
                <div
                  className="pr-application-card-watch inline-flex w-fit max-w-full items-center gap-1.5 border-l-2 border-[var(--c-accent)] pl-2 font-['DM_Mono'] text-[11px] font-medium uppercase tracking-[0.08em] leading-none text-[var(--c-accent)]"
                  title="Worth watching"
                  aria-label="Worth watching"
                >
                  <Flag size={11} strokeWidth={2} className="shrink-0" />
                  <span className="min-w-0 truncate">Worth watching</span>
                </div>
              )}

              {!sourceOnly && <PlanningSignalChips
                application={currentApp as Record<string, unknown>}
                category={categoryInfo}
                surface="card"
                maxVisible={3}
                dense
              />}

              {/* Description */}
              <p className="pr-application-card-description text-sm leading-relaxed line-clamp-3 text-[var(--c-text-secondary)]">{displayDescription}</p>

              {/* Footer — uniform h-7 elements throughout */}
              <div className="pr-application-card-actions flex items-center justify-between gap-2 pt-2 border-t border-[var(--c-border)]/40">
                <p className="min-w-0 font-mono text-[11px] leading-tight text-[var(--c-text-secondary)]">
                  {feedDateLabel}
                </p>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {/* Report */}
                  {!sourceOnly && <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <motion.button
                          onClick={(e) => { e.stopPropagation(); openApplicationModal(currentApp, { initialTab: 'reports' }); }}
                          className="pr-card-icon-button inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)] transition-all duration-150 hover:border-[var(--c-accent)]/50 hover:text-[var(--c-accent)] sm:h-7 sm:w-7"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          aria-label="Generate planning report"
                        >
                          <Newspaper size={13} />
                        </motion.button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[var(--dock-bg)] border-[var(--dock-border)] text-[var(--c-text-primary)]">
                        Open AI-generated planning report
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  }
                  {/* ── Combined Share + Open button ── */}
                  <div className="pr-card-action-group flex h-11 overflow-hidden rounded-[3px] border border-[var(--c-text-primary)] sm:h-7">
                    <button
                      className="pr-card-action-icon flex w-11 shrink-0 items-center justify-center border-r border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)] transition-colors hover:text-[var(--c-text-primary)] focus:outline-none sm:w-9"
                      onClick={(e) => { e.stopPropagation(); setShareOpen(true); }}
                      aria-label="Share planning application"
                    >
                      <Share2 size={13} />
                    </button>

                    <button
                      className="pr-card-open-button flex min-h-11 items-center justify-center gap-1.5 bg-[var(--c-text-primary)] px-3 text-xs font-semibold text-[var(--c-bg)] transition-opacity hover:opacity-85 focus:outline-none sm:min-h-0 sm:px-3"
                      onClick={(e) => { e.stopPropagation(); openApplicationModal(currentApp); }}
                      aria-label="Open details"
                    >
                      Open details
                    </button>
                  </div>

                  {/* Share modal — headless, controlled by seesaw button */}
                  <ShareButton
                    application={{ reference: safeReferenceNo, description: originalDescription, aiSummary: ai_summary, location: safeAddress, status: status || 'Status Unknown' }}
                    documents={documentsList.map(d => ({...d, document_url: d.doc_link}))}
                    shareUrl={modalShareUrl}
                    isOpen={shareOpen}
                    onOpenChange={setShareOpen}
                    hideDefaultTrigger
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Reference band ── */}
      <div className="pr-card-reference-band flex items-center justify-between gap-2 border-t border-[var(--c-border)] bg-[var(--c-bg-secondary)] px-3 py-2">
        <EnhancedReferenceDisplay
          referenceNo={safeReferenceNo}
          council={currentAppCouncil}
          className="min-w-0 flex-1"
          onOpenDetails={() => openApplicationModal(currentApp)}
        />
        <div className="pr-card-reference-nav flex shrink-0 items-center gap-0.5">
          {apps.length > 1 ? (
            <>
              <motion.button
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                className="pr-card-reference-nav-button w-6 h-6 rounded-full flex items-center justify-center text-[var(--c-text-secondary)] hover:text-[var(--c-accent)] hover:bg-[var(--c-surface-hover)] transition-all duration-200"
                whileTap={{ scale: 0.9 }}
                aria-label="Previous application"
              >
                <ChevronLeft size={14} />
              </motion.button>
              <span className="text-[10px] font-mono text-[var(--c-text-secondary)] px-1 whitespace-nowrap">
                {currentIndex + 1}/{apps.length} apps
              </span>
              <motion.button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="pr-card-reference-nav-button w-6 h-6 rounded-full flex items-center justify-center text-[var(--c-text-secondary)] hover:text-[var(--c-accent)] hover:bg-[var(--c-surface-hover)] transition-all duration-200"
                whileTap={{ scale: 0.9 }}
                aria-label="Next application"
              >
                <ChevronRight size={14} />
              </motion.button>
            </>
          ) : (
            <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap font-['DM_Mono'] text-[11px] leading-none text-[var(--c-text-tertiary)]">
              1 app
            </span>
          )}
        </div>
      </div>

      {/* ── Discussion / Comments Section ── */}
      {discussionsEnabled ? (
        <div
          className="pr-card-discussion border-t border-[var(--c-border)] bg-[var(--c-bg)]/55"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pr-card-discussion-header flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="pr-card-discussion-icon inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/12 text-[var(--c-accent)]">
                <MessageSquareText size={13} />
              </span>
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-secondary)]">
                  Discussion
                </p>
                <p className="truncate text-[11px] text-[var(--c-text-tertiary)]">
                  {discussionCount > 0
                    ? `${discussionCountLabel} · ${discussionThreadLabel}`
                    : 'No comments yet'}
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              <motion.button
                type="button"
                onClick={handleOpenDiscussion}
                disabled={!safeReferenceNo}
                className="pr-card-secondary-button inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1.5 text-[11px] font-medium text-[var(--c-text-primary)] transition-colors duration-150 hover:border-[var(--c-accent)]/50 hover:text-[var(--c-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                whileTap={!safeReferenceNo ? undefined : { scale: 0.96 }}
                aria-label={discussionCount > 0 ? 'Open full discussion' : 'Start the discussion'}
              >
                {discussionCount > 0 ? 'Open' : 'Start'}
                <ChevronRight size={12} />
              </motion.button>
            </div>
          </div>

          {discussionCount > 0 && discussionPreviewComments.length > 0 ? (
            <div
              className="pr-card-discussion-preview group/cardpreview relative border-t border-[var(--c-border)]/70 px-3 pb-3 pt-2.5 sm:px-4"
              role="button"
              tabIndex={0}
              onClick={handleOpenDiscussion}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenDiscussion(e as unknown as React.MouseEvent);
                }
              }}
            >
              <div>
                {(() => {
                  const top = discussionPreviewComments[0];
                  if (!top) return null;
                  return (
                    <article
                      key={top.id}
                      className="pr-card-discussion-comment group/preview flex gap-2.5 rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)]/90 p-2.5 transition-colors hover:border-[var(--c-accent)]/40"
                    >
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/10 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--c-accent)]">
                        {getDiscussionAuthorInitials(top.author.displayName)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <span className="rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/8 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[var(--c-accent)]">
                            Top
                          </span>
                          <span className="truncate font-medium text-[var(--c-text-primary)]">
                            {top.author.displayName}
                          </span>
                          <span className="text-[var(--c-text-tertiary)]">·</span>
                          <span className="flex-shrink-0 text-[var(--c-text-tertiary)]">
                            {formatDiscussionPreviewTimestamp(top.createdAt)}
                          </span>
                        </div>
                        <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-[var(--c-text-secondary)]">
                          {top.status === 'deleted' ? 'Comment deleted.' : top.body}
                        </p>
                        {(top.replyCount > 0 || (discussionCount - 1) > 0) ? (
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-medium text-[var(--c-text-tertiary)]">
                            {top.replyCount > 0 ? (
                              <span className="inline-flex items-center gap-1">
                                <MessageSquareText size={9} />
                                {top.replyCount} {top.replyCount === 1 ? 'reply' : 'replies'}
                              </span>
                            ) : null}
                            {discussionCount - 1 > 0 ? (
                              <span>+{discussionCount - 1} more in thread</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })()}
              </div>
            </div>
          ) : discussionCount === 0 ? (
            <div className="pr-card-discussion-empty border-t border-[var(--c-border)]/70 px-3 py-3 sm:px-4">
              <div
                role="button"
                tabIndex={0}
                onClick={handleOpenDiscussion}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpenDiscussion(e as unknown as React.MouseEvent);
                  }
                }}
                className="pr-card-discussion-empty-button flex items-center justify-between gap-3 rounded-2xl border border-dashed border-[var(--c-border)] bg-[var(--c-surface)]/55 px-3 py-2.5 text-left transition-colors hover:border-[var(--c-accent)]/40 hover:bg-[var(--c-surface)]/80"
              >
                <p className="text-[11px] leading-snug text-[var(--c-text-secondary)]">
                  Be the first to share your view on this application.
                </p>
                <ChevronRight size={12} className="flex-shrink-0 text-[var(--c-text-tertiary)]" />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="px-3 pb-3 sm:px-4 sm:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div className="p-3 sm:p-4 rounded-[var(--radius-md)] bg-[var(--c-bg)] border border-[var(--c-border)]">
                <h4 className="label mb-2 sm:mb-3 text-[var(--c-text-primary)]">Application Details</h4>
                <div className="text-[10px] xs:text-xs space-y-2 sm:space-y-3">
                  <p className="body-m leading-relaxed text-[var(--c-text-secondary)]">{originalDescription}</p>

                {currentApp.ai_match_data && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-400/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-purple-400">🏗️</span>
                      <h5 className="font-medium text-purple-300">Project Information</h5>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="text-[var(--c-text-secondary)]">Development Name:</span>
                        <span className="ml-2 text-purple-300 font-medium">{currentApp.ai_match_data.development_name}</span>
                      </div>
                      {currentApp.ai_match_data.development_aliases && currentApp.ai_match_data.development_aliases.length > 0 && (
                        <div>
                          <span className="text-[var(--c-text-secondary)]">Also Known As:</span>
                          <div className="ml-2 mt-1">
                            {currentApp.ai_match_data.development_aliases.map((alias, idx) => (
                              <span key={idx} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-[10px]">{alias}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {currentApp.ai_match_data.is_combined_application && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-purple-300">⚡ This is a combined application covering multiple parts of the development</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-2 sm:mt-3 grid grid-cols-2 gap-2 sm:gap-3">
                  <div>
                    <div className="font-medium label text-[var(--c-text-primary)]">Status:</div>
                    <div className={`${statusColor.text} text-xs`}>{displayStatus}</div>
                  </div>
                  <div>
                    <div className="font-medium label text-[var(--c-text-primary)]">Reference No:</div>
                    <div className="font-mono text-xs text-[var(--c-text-secondary)]">
                      {formatCouncilQualifiedReference(safeReferenceNo, currentAppCouncil)}
                    </div>
                  </div>
                </div>

                <div className="mt-2 sm:mt-3">
                  <div className="font-medium label text-[var(--c-text-primary)]">Location:</div>
                  <div className="text-xs text-[var(--c-text-secondary)] mt-1">{safeAddress}</div>
                </div>

                <div className="mt-2 sm:mt-3">
                  <div className="font-medium label text-[var(--c-text-primary)]">Dates:</div>
                  <div className="grid grid-cols-2 gap-x-2 sm:gap-x-3 mt-1 sm:mt-2 text-xs text-[var(--c-text-secondary)]">
                    <div>Received: {formatDate(received_date)}</div>
                    <div>Validated: {formatDate(validated_date)}</div>
                  </div>
                </div>

                {documentsList.length > 0 && (
                  <div className="mt-2 sm:mt-3">
                    <div className="font-medium label text-[var(--c-text-primary)] mb-1 sm:mb-2">Documents:</div>
                    <div className="grid grid-cols-2 xs:grid-cols-3 gap-2">
                      {documentsList.slice(0, 4).map((doc, idx) => {
                        const rawDocThumbnail = doc.thumbnail_url?.trim() || '';
                        const docThumbnailSrc = getThumbnailSrc(doc.thumbnail_url);
                        const docUrl = getPlanningDocumentUrl(doc);
                        return (
                          <div key={idx} className="aspect-video bg-[var(--c-bg)] rounded-[var(--radius-sm)] overflow-hidden border border-[var(--c-border)] hover:border-[var(--c-accent)] transition-colors duration-200 relative">
                            {docUrl ? (
                              <div className="block w-full h-full cursor-pointer relative" onClick={(e) => handleDocumentClick(doc, currentApp, e)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDocumentClick(doc, currentApp); } }} tabIndex={0} role="button" aria-label="Open application image in details">
                                <Image src={docThumbnailSrc} alt="Open application image" fill={true} style={{ objectFit: 'cover' }} unoptimized={true} />
                              </div>
                            ) : (
                              <div className="w-full h-full cursor-pointer" onClick={(e) => openApplicationModalWithDocument(currentApp, rawDocThumbnail, e)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openApplicationModalWithDocument(currentApp, rawDocThumbnail); } }} tabIndex={0} role="button" aria-label="Open application image in details">
                                <Image src={docThumbnailSrc} alt="Document thumbnail" fill={true} style={{ objectFit: 'cover' }} unoptimized={true} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {documentsList.length === 0 && (
                      <div className="text-[var(--c-text-secondary)] text-xs italic">No documents available</div>
                    )}
                    {documentsList.length > 4 && (
                      <div className="mt-2 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openApplicationModal(currentApp);
                          }}
                          className="text-xs text-[var(--c-accent)] hover:text-[var(--c-accent-muted)] transition-colors duration-200"
                        >
                          +{documentsList.length - 4} more documents
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {link && link !== "#" && (
                  <div className="pt-2 sm:pt-3">
                    <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--c-accent)] hover:text-[var(--c-accent-muted)] underline transition-colors duration-200">
                      View on Council Website <span>→</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(GroupedApplicationsCard);
